import { MdCheck, MdContentCopy, MdOpenInNew } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { useState, type ComponentType } from 'react';
import { Form, ProgressBar } from 'react-bootstrap';
import SubtleBadge from '@/components/common/SubtleBadge';
import SmallButton from '@/app/common/components/SmallButton';
import URLRendererWithLink from '@/app/common/components/CellRenderers/URLRendererWithLink';
import formatUtcDate from '@/helpers/formatUtcDate';
import { errorMessage, successMessage } from '@/utils/toast';
import { useUpdateAnalysisVisibilityMutation } from '@/app/PageSpeed/api/analysisApi';
import healthScoreColorAndLabel from '@/helpers/healthScoreColorAndLabel';
import type { AnalysisHistoryItem } from '@/app/PageSpeed/api/types/analysisApiTypes';

const TypedURLRendererWithLink = URLRendererWithLink as ComponentType<any>;

const publicReportUrl = (slug?: string | null) =>
  slug
    ? `${import.meta.env.VITE_REACT_APP_MIZFA_TOOLS_PANEL_URL}/reporting/page_speed/${slug}`
    : '';

export const pageSpeedReportColumnConfig = (
  domainSlug?: string,
  domain?: string
): any[] => [
  {
    field: 'url',
    title: 'آدرس',
    type: 'string',
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    minWidth: 260,
    flex: 1,
    cellRenderer: (params: { value?: string; data: AnalysisHistoryItem }) => (
      <TypedURLRendererWithLink
        url={params.value}
        to={
          params.data.task_id || params.data._id
            ? `/analysis/overview/${params.data.task_id || params.data._id}/${domainSlug}/${domain}`
            : undefined
        }
      />
    )
  },
  {
    field: 'site_health',
    title: 'سلامت سایت',
    type: 'number',
    width: 165,
    cellRenderer: (params: { value?: number }) => {
      const score = params.value;
      if (typeof score !== 'number') return '-';

      const health = healthScoreColorAndLabel(score);
      return (
        <div className="d-flex gap-1 align-items-center">
          <span>{Math.round(score)}%</span>
          <ProgressBar
            style={{ height: 5 }}
            className="rounded-pill flex-grow-1"
            variant={health.prClass}
            now={score}
          />
        </div>
      );
    }
  },
  {
    field: 'is_public',
    title: 'نوع گزارش',
    type: 'boolean',
    width: 125,
    cellRenderer: (params: { value?: boolean }) => (
      <div className="w-100 fs-0 h-100 d-flex align-items-center justify-content-center">
        <SubtleBadge bg={params.value ? 'info' : 'danger'}>
          {params.value ? 'عمومی' : 'خصوصی'}
        </SubtleBadge>
      </div>
    )
  },
  {
    field: 'created_at',
    title: 'تاریخ ایجاد',
    type: 'string',
    width: 145,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    valueFormatter: (params: { value?: string }) => formatUtcDate(params.value)
  },
  {
    field: 'actions',
    title: 'عملیات',
    type: 'array',
    width: 250,
    pinned: 'left',
    sortable: false,
    searchable: false,
    noFilter: true,
    cellRenderer: (params: { data: AnalysisHistoryItem }) => {
      const [copied, setCopied] = useState(false);
      const [isPublic, setIsPublic] = useState(
        Boolean(params.data.is_public)
      );
      const [updateVisibility, { isLoading: isUpdatingVisibility }] =
        useUpdateAnalysisVisibilityMutation();
      const taskId = params.data.task_id || params.data._id;
      const reportUrl = publicReportUrl(params.data.slug);
      const detailsUrl = `/analysis/overview/${taskId}/${domainSlug}/${domain}`;
      const openUrl = reportUrl || detailsUrl;

      const changeVisibility = async (nextIsPublic: boolean) => {
        if (!taskId) return;

        try {
          const response = await updateVisibility({
            task_id: taskId,
            is_public: nextIsPublic
          }).unwrap();
          const responseData =
            response && typeof response === 'object' && 'data' in response
              ? response.data
              : null;
          const nextSlug =
            responseData &&
            typeof responseData === 'object' &&
            'slug' in responseData &&
            typeof responseData.slug === 'string'
              ? responseData.slug
              : params.data.slug;

          params.data.is_public = nextIsPublic;
          params.data.slug = nextSlug;
          setIsPublic(nextIsPublic);
          successMessage(nextIsPublic ? 'گزارش عمومی شد.' : 'گزارش خصوصی شد.');
        } catch {
          errorMessage('تغییر وضعیت گزارش ناموفق بود.');
        }
      };

      const copyUrl = () => {
        navigator.clipboard
          .writeText(openUrl)
          .then(() => {
            setCopied(true);
            successMessage('پیوند گزارش کپی شد.');
            window.setTimeout(() => setCopied(false), 2500);
          })
          .catch(() => errorMessage('کپی پیوند گزارش ناموفق بود.'));
      };

      return (
        <div className="d-flex h-100 align-items-center justify-content-center gap-2">
          <SmallButton onClick={copyUrl} title="کپی پیوند">
            {copied ? (
              <MdCheck size={16} style={{ width: 16, height: 16 }} />
            ) : (
              <MdContentCopy size={16} style={{ width: 16, height: 16 }} />
            )}
          </SmallButton>
          <SmallButton
            as={Link}
            onClick={undefined}
            to={openUrl}
            target={reportUrl ? '_blank' : undefined}
            rel={reportUrl ? 'noreferrer' : undefined}
            title="بازکردن گزارش"
          >
            <MdOpenInNew size={16} style={{ width: 16, height: 16 }} />
          </SmallButton>
          <Form.Select
            size="sm"
            aria-label="نوع گزارش"
            value={isPublic ? 'public' : 'private'}
            disabled={isUpdatingVisibility}
            onChange={event =>
              changeVisibility(event.target.value === 'public')
            }
          >
            <option value="public">عمومی</option>
            <option value="private">خصوصی</option>
          </Form.Select>
        </div>
      );
    }
  }
];
